import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    CustomReportTemplatesDocument,
    ReportTemplateEntityType,
    type CustomReportTemplatesQuery,
    type CustomReportTemplatesQueryVariables,
} from '@/graphql/types';
import { isReportTemplateEntityType } from '../../../typeGuards';
import { translateEntityType } from '../../../enums';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { RuntimeDataService } from '../types';
import type {
    RuntimeDataServiceRequest,
    RuntimeResultLinkDescriptorTabular,
    RuntimeTabularColumnDescriptor,
} from '../types';
import { isRecord, normalizeArrayEntries } from '../validationPrimitives';

type TemplateNode = NonNullable<
    NonNullable<NonNullable<CustomReportTemplatesQuery['reportTemplates']>['edges'][number]>['node']
>;

function validateTemplateOptionsInput(input: unknown): RuntimeDataServiceInput<'template.options'> {
    if (!isRecord(input)) {
        throw new Error('Expected "template.options" input to be an object.');
    }

    if (typeof input.entityType === 'undefined') {
        return {};
    }

    if (!isReportTemplateEntityType(input.entityType)) {
        throw new Error(
            'Expected "template.options.entityType" to be a valid ReportTemplateEntityType value when provided.'
        );
    }

    return {
        entityType: input.entityType,
    };
}

function validateTemplateOptionsResult(
    result: unknown
): RuntimeDataServiceOutput<'template.options'> {
    return normalizeArrayEntries(result, 'template.options', (entry, entryPath) => {
        if (!isRecord(entry)) {
            throw new Error(`Expected "${entryPath}" to be an object.`);
        }

        const { id, name, entityType } = entry;

        if (typeof id !== 'string' || id.trim().length === 0) {
            throw new Error(`Expected "${entryPath}.id" to be a non-empty string.`);
        }
        const normalizedId = id.trim();
        const normalizedName =
            typeof name === 'string' && name.trim().length > 0 ? name.trim() : normalizedId;

        if (!isReportTemplateEntityType(entityType)) {
            throw new Error(
                `Expected "${entryPath}.entityType" to be a valid ReportTemplateEntityType value.`
            );
        }

        return {
            id: normalizedId,
            name: normalizedName,
            entityType,
        };
    });
}

function parseTemplateOptionsArgToEntityType(
    arg: string | null
): ReportTemplateEntityType | undefined {
    if (arg === null) {
        return undefined;
    }

    if (arg === 'project') {
        return ReportTemplateEntityType.Project;
    }

    if (arg === 'measuring-point') {
        return ReportTemplateEntityType.MeasuringPoint;
    }

    throw new Error(
        'Expected template options binding "arg" to be "project" or "measuring-point".'
    );
}

function createTemplateOptionsColumns() {
    return [
        {
            kind: 'text',
            key: 'id',
            label: gettext('ID'),
            defaultAlignment: 'left',
            resolveCell: (entry: RuntimeDataServiceOutput<'template.options'>[number]) => entry.id,
        },
        {
            kind: 'text',
            key: 'name',
            label: gettext('NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: RuntimeDataServiceOutput<'template.options'>[number]) =>
                entry.name,
        },
        {
            kind: 'text',
            key: 'entityType',
            label: gettext('ENTITY_TYPE'),
            defaultAlignment: 'left',
            resolveCell: (entry: RuntimeDataServiceOutput<'template.options'>[number]) =>
                entry.entityType,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<
        RuntimeDataServiceOutput<'template.options'>[number]
    >[];
}

export class TemplateOptionsRuntimeDataService extends RuntimeDataService<'template.options'> {
    static readonly serviceId = 'template.options' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_TEMPLATE_OPTIONS_LABEL'),
            description: gettext('DATA_SERVICE_TEMPLATE_OPTIONS_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'options',
                label: gettext('DATA_SERVICE_TEMPLATE_OPTIONS_LINK_LABEL'),
                description: gettext('DATA_SERVICE_TEMPLATE_OPTIONS_LINK_DESCRIPTION'),
                supportedContextKinds: ['project', 'measuring-point'],
                supportedSwarmTypes: null,
                path: '',
                valueKind: 'tabular',
                argPolicy: 'required',
                exposeInInsertionPresets: false,
                columns: createTemplateOptionsColumns(),
                parseTableRows: validateTemplateOptionsResult,
                buildInput: ({ context, arg }) => {
                    const entityTypeFromArg = parseTemplateOptionsArgToEntityType(arg);
                    if (entityTypeFromArg) {
                        return { entityType: entityTypeFromArg };
                    }

                    if (!context) {
                        throw new Error(
                            'Template options binding requires context when "arg" is not provided.'
                        );
                    }

                    return {
                        entityType:
                            context.kind === 'project'
                                ? ReportTemplateEntityType.Project
                                : ReportTemplateEntityType.MeasuringPoint,
                    };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'template.options',
            RuntimeDataServiceOutput<'template.options'>[number]
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateTemplateOptionsInput;

    validateResult = validateTemplateOptionsResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'template.options'>) =>
        from(
            this.client.query<CustomReportTemplatesQuery, CustomReportTemplatesQueryVariables>({
                query: CustomReportTemplatesDocument,
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data?.reportTemplates) {
                    throw new Error(
                        'Missing reportTemplates payload while listing template options.'
                    );
                }

                const options: RuntimeDataServiceOutput<'template.options'> =
                    data.reportTemplates.edges
                        .map((edge) => edge?.node)
                        .filter((node): node is TemplateNode => node != null)
                        .map((node) => ({
                            id: node.id,
                            name: node.name,
                            entityType: node.entityType,
                        }))
                        .filter((entry) =>
                            input.entityType ? entry.entityType === input.entityType : true
                        )
                        .sort((left, right) => {
                            const nameCompare = left.name.localeCompare(right.name, undefined, {
                                sensitivity: 'base',
                            });

                            if (nameCompare !== 0) {
                                return nameCompare;
                            }

                            if (left.entityType !== right.entityType) {
                                return translateEntityType(left.entityType).localeCompare(
                                    translateEntityType(right.entityType)
                                );
                            }

                            return left.id.localeCompare(right.id);
                        });

                return options;
            })
        );
}

export function createTemplateOptionsService(
    client: ApolloClient<object>
): TemplateOptionsRuntimeDataService {
    return new TemplateOptionsRuntimeDataService(client);
}
